/**
 * Pure functions on NFC readings (T-174, ADR-0143): the URL of an NDEF record and the UID of a tag in hexadecimal
 * notation. The app sends a workplace tag's URL to the server, which checks its SUN message; it never decides about it.
 */

/** An NDEF record as the NFC plugin delivers it (bytes as numbers). */
export interface NdefRecordData {
  /** Type Name Format (1 = NFC Forum well-known type, 3 = absolute URI). */
  readonly tnf: number;
  /** Record type. */
  readonly type: readonly number[];
  /** Payload. */
  readonly payload: readonly number[];
}

/** Abbreviations of the URI record type definition of the NFC Forum, by their identifier code. */
const uriPrefixes: readonly string[] = [
  "",
  "http://www.",
  "https://www.",
  "http://",
  "https://",
  "tel:",
  "mailto:",
  "ftp://anonymous:anonymous@",
  "ftp://ftp.",
  "ftps://",
  "sftp://",
  "smb://",
  "nfs://",
  "ftp://",
  "dav://",
  "news:",
  "telnet://",
  "imap:",
  "rtsp://",
  "urn:",
  "pop:",
  "sip:",
  "sips:",
  "tftp:",
  "btspp://",
  "btl2cap://",
  "btgoep://",
  "tcpobex://",
  "irdaobex://",
  "file://",
  "urn:epc:id:",
  "urn:epc:tag:",
  "urn:epc:pat:",
  "urn:epc:raw:",
  "urn:epc:",
  "urn:nfc:",
];

/** Type Name Format of a well-known record. */
const wellKnownFormat = 1;

/** Type Name Format of an absolute URI record. */
const absoluteUriFormat = 3;

/** Record type „U“ of a URI record. */
const uriType = 0x55;

/**
 * Decodes bytes as UTF-8; invalid sequences become replacement characters.
 *
 * @param bytes - The bytes.
 * @returns The text.
 */
function utf8(bytes: readonly number[]): string {
  const text = new TextDecoder().decode(Uint8Array.from(bytes));
  return text;
}

/**
 * Returns the URI of the first URI record of an NDEF message (a well-known record of type „U“ or an absolute URI).
 *
 * @param records - The records of the message, or `null`.
 * @returns The URI, or `null` if the message has none.
 */
export function ndefUri(records: readonly NdefRecordData[] | null | undefined): string | null {
  for (const record of records ?? []) {
    const wellKnownUri =
      record.tnf === wellKnownFormat && record.type.length === 1 && record.type[0] === uriType;

    if (wellKnownUri && record.payload.length > 0) {
      const prefix = uriPrefixes[record.payload[0] ?? 0] ?? "";
      const uri = prefix + utf8(record.payload.slice(1));
      return uri;
    }

    if (record.tnf === absoluteUriFormat && record.type.length > 0) {
      const uri = utf8(record.type);
      return uri;
    }
  }

  return null;
}

/**
 * Formats the bytes of a UID in upper-case hexadecimal notation without separators.
 *
 * @param bytes - The bytes (signed values of Java bytes are accepted).
 * @returns The UID, or `null` without bytes.
 */
export function uidHex(bytes: readonly number[] | null | undefined): string | null {
  if (bytes === null || bytes === undefined || bytes.length === 0) {
    return null;
  }

  const hex = bytes
    .map((value) => (value & 0xff).toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();

  return hex;
}
